import {
  takeLatest,
  put,
  all,
  call,
  fork,
  select,
  delay,
} from "redux-saga/effects";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { actions } from "@reducers/configReducer";
import { actions as authActions } from "@reducers/authReducer";
import { initRemoteConfig } from "@services/firebase";
import HyperSdkReact from "hyper-sdk-react";
import { actions as categoryActions } from "@reducers/categoriesReducer";
import { actions as refreshActions } from "@reducers/refreshReducer";
import { actions as cartActions } from "@reducers/cartReducer";
import { actions as supportActions } from "@reducers/supportReducer";
import { actions as homeWidgetActions } from "@reducers/homeWidgetReducer";
import setAppConfigs from "@lib/setAppConfigs";
import {
  getDropDownOptions,
  getAppSettings,
  getAppBootParams,
} from "@services/api";
import { getData } from "@lib/storage";
import { AppEventsLogger } from "react-native-fbsdk-next";
import crashlytics from "@react-native-firebase/crashlytics";
import * as Sentry from "@sentry/react-native";
import analytics from "@services/analytics";
import clTaskAction from "@actions/clTask.action";
import { SHOW_IJ_FLOW } from "@constants/strings";
import notificationPermission from "@lib/logNotificationPermission";
import initMovex from "@lib/initMovex";
import getTokenZ from "@utils/getTokenZ";
import { getHomePageNew } from "@services/cmsV2-api";
// Plotline removed
import {
  GET_APP_SETTINGS,
  SET_APP_SETTINGS,
  GET_APP_BOOT_PARAMS,
  SYNC_USER_ACTIONS,
  SET_LANGUAGE,
  INVOKE_APP_SETUP,
  GET_USER_DETAILS_BY_TOKEN_REQUEST,
  CLEAR_APP_SETUP,
  SET_JUSPAY_INIT_DATA,
  SET_JUSPAY_PREFETCH_DATA,
  SET_APPLINK,
} from "../reducers/actionTypes";
import { configureApplinks, setAppLink } from "./configureApplinks";
import trackVisibleApps from "./utils/appVisibilityTracker";

export const setupCrashlytics = ({ userId, attributes }) => {
  return Promise.all([
    crashlytics().setUserId(userId),
    crashlytics().setAttributes(attributes),
  ]);
};

export function* setSentryCrashlytics(data) {
  // replace call -> fork non blocking, limit these calls..
  // if (!data.name) return;
  yield call(Sentry.setUser, {
    id: data.id,
    username: data.name,
  });
  yield call(analytics.setUser, data.id, data);
  yield call(AppEventsLogger.setUserID, data.id);

  yield call(setupCrashlytics, {
    userId: data.id,
    attributes: {
      user_name: String(data.name),
      phone_number: String(data.phone_number),
    },
  });

  // add context for sentry here....
  const otherInfo = {
    phone_number: data.phone_number,
    version_code: data.version_code,
    version_name: data.version_name,
  };
  Sentry.setContext("otherInfo", otherInfo);
}

function* invokeAppSetup() {
  try {
    /* setup channel subscription */
    yield fork(configureApplinks);
    yield call(fetchSyncUser);

    yield put({ type: "INVOKE_COMPLETE" });
  } catch (e) {
    yield put(actions.failureSyncUser(e));
  }
}

function* clearAppSetup() {
  // Perform app unmounting cleanup actions here
  yield "cleanup complete";
  console.log("clearAppSetup");
}

function* getAppSettingsSaga() {
  try {
    const { data } = yield call(getAppSettings);
    if (data) {
      yield put({
        type: SET_APP_SETTINGS,
        payload: data,
      });
    }
  } catch (error) {
    console.log(error);
  }
}

function* setupRemoteConfig() {
  yield fork(initRemoteConfig);
}

function* getAppBootParamsSaga() {
  try {
    const { data } = yield call(getAppBootParams);
    if (data) {
      yield put({
        type: SET_APP_SETTINGS,
        payload: data.appSettings,
      });
    }
  } catch (error) {
    console.log(error);
  }
}

function* configureJuspay({ juspayInitiateData, juspayPrefetchData }) {
  if (juspayPrefetchData && typeof juspayPrefetchData === "object") {
    const juspayStatus = yield select(
      (state) => state.configReducer.juspayStatus,
    );

    if (!juspayStatus.preFetch) {
      HyperSdkReact.preFetch(JSON.stringify(juspayPrefetchData));
      yield put(actions.updateJuspayStatus({ preFetch: true }));
      yield put({
        type: SET_JUSPAY_PREFETCH_DATA,
        payload: {
          juspayPrefetchData,
        },
      });
    }

    if (!juspayStatus.createHyperServices) {
      HyperSdkReact.createHyperServices();
      yield put(actions.updateJuspayStatus({ createHyperServices: true }));
    }
    if (!juspayStatus.initiate) {
      if (juspayInitiateData && typeof juspayInitiateData === "object") {
        HyperSdkReact.initiate(JSON.stringify(juspayInitiateData));
        yield put(actions.updateJuspayStatus({ initiate: true }));
        yield put({
          type: SET_JUSPAY_INIT_DATA,
          payload: {
            juspayInitiateData,
          },
        });
      }
    }
  }
}

function* setBootParams(data) {
  const {
    businessCard,
    ratingCheck,
    version,
    homePage,
    checkClOrderRating,
    banners,
    oosSku,
    appSettings,
    onboardingSupportNumber,
    currentMonthStats,
    categoryList = [],
    whatsAppMsg,
    fullCategoryList = {},
    ReplaceReasons,
    ReturnReasons,
    walkthrough,
    juspayInitiateData,
    juspayPrefetchData,
    feedStaleDelayMs,
    todayDailyTasks,
    earlyJourney,
    showVacationModal,
    vacationDetails,
    loggedInDevicesSummary,
    isNewShopPerformanceEnabled,
    isStarCp,
    isStarCpHasActiveDbs,
    isStarCpHasActiveRoutesV2,
    isVacationEnabled,
    isSortingEnabled,
    isWalletEnabled,
    partialDeliveryEnabled,
    multiOrderDelivery,
    disable_add_db_btn,
    pollingEnable,
    pollingInterval,
    features = {},
    scannerModes = {},
  } = data;

  try {
    if (earlyJourney && earlyJourney?.enableIJ) {
      const enableIJJson = yield AsyncStorage.getItem(SHOW_IJ_FLOW);
      if (
        enableIJJson === undefined ||
        enableIJJson === null ||
        JSON.parse(enableIJJson) === true
      ) {
        earlyJourney.enableIJ = true;
      } else {
        earlyJourney.enableIJ = false;
      }
    }
  } catch (e) {
    Sentry.captureException(e);
  }

  yield put(
    actions.successSyncUser({
      businessCard,
      ratingCheck,
      version,
      homePage,
      checkClOrderRating,
      banners,
      oosSku,
      appSettings,
      walkthrough,
      onboardingSupportNumber,
      currentMonthStats,
      whatsAppMsg,
      ReplaceReasons,
      ReturnReasons,
      categories: fullCategoryList.result || [],
      juspayInitiateData,
      juspayPrefetchData,
      earlyJourney,
      isNewShopPerformanceEnabled,
      isStarCp,
      isStarCpHasActiveDbs,
      isStarCpHasActiveRoutesV2,
      isVacationEnabled,
      isSortingEnabled,
      isWalletEnabled,
      partialDeliveryEnabled,
      multiOrderDelivery,
      disable_add_db_btn,
      pollingEnable,
      pollingInterval,
      features,
      scannerModes,
    }),
  );

  if (loggedInDevicesSummary) {
    yield put(authActions.setLoggedInOptions(loggedInDevicesSummary));
  }

  if (showVacationModal) {
    yield put(
      authActions.updateVacationModalData({
        showVacationModal,
        vacationDetails,
      }),
    );
  }

  if (todayDailyTasks) {
    yield put(clTaskAction.getTodayDailyTasks.success(todayDailyTasks, null));
  } else {
    yield put(clTaskAction.getTodayDailyTasks.success({}, null));
  }

  if (categoryList) {
    yield put(
      categoryActions.gegetAllPrimaryCategoriesWithProductTypesSuccess(
        categoryList,
      ),
    );
  }

  yield fork(configureJuspay, { juspayInitiateData, juspayPrefetchData });
  yield call(setupRemoteConfig);
  if (feedStaleDelayMs) {
    yield put(refreshActions.setDelayDuration(feedStaleDelayMs));
  }
}

function* fetchSyncUser({ payload: { userToken, bootParams } = {} } = {}) {
  try {
    const token = userToken || (yield call(getTokenZ));

    const bootParamsData = bootParams || (yield call(getAppBootParams)).data;
    const homePage = (yield call(getHomePageNew, {
      limit: 10,
      offset: 0,
    })).data;
    bootParamsData.homePage = homePage;
    yield fork(setBootParams, bootParamsData);
    if (bootParamsData.meResponse) {
      if (token) {
        const {
          data: { supportTicket, clDropDownOptions },
        } = yield call(getDropDownOptions);
        yield put(
          authActions.setTeamLeaderDetails({
            ...bootParamsData.meResponse,
            earlyJourney: bootParamsData.earlyJourney,
            supportTicket,
            clDropDownOptions,
            orderMetricsEnabled: bootParamsData.orderMetricsEnabled,
          }),
        );
        // Plotline setup removed
      }

      const is_suspended =
        bootParamsData?.meResponse?.is_suspended ||
        bootParamsData?.meResponse?.leader_state === "LEADER_SUSPENDED";
      if (is_suspended) {
        yield put(authActions.showSuspensionModal(is_suspended || false));
        yield put(supportActions.getSupportConfigs());
      }
      yield fork(setSentryCrashlytics, bootParamsData?.meResponse);
      // Defer movex geo-tracking activation off the login->home mount window.
      // initMovex starts movex 2.0's native foreground service + per-fix
      // location-event emission; firing it here (one line above the
      // fetchHomePageWidget dispatch below) spun that native activity up
      // concurrently with the initial home-widget mount and the auth->home
      // transition, aggravating a Fabric mount race into fatal
      // RetryableMountingLayerException (Sentry PARTNER-APP-7A7; the crash
      // count stepped up ~10x in 1.56.6 when tracking was first started here).
      // A few seconds' delay is imperceptible for background geo-tracking and
      // moves activation clear of the transition.
      yield fork(function* deferInitMovex() {
        yield delay(4000);
        yield call(initMovex, bootParamsData?.meResponse?.id);
      });
    } else if (token) {
      yield put({ type: GET_USER_DETAILS_BY_TOKEN_REQUEST });
    }
    let selectedContact = yield select(
      ({ customer }) => customer?.selectedContact,
    );
    if (!selectedContact) {
      selectedContact = yield call(getData, "selectedContact");
    }
    if (selectedContact && token) {
      yield put(cartActions.viewCartLiteRequest(selectedContact));
    }
    if (bootParamsData.minimumPostViewTime) {
      yield put(
        actions.setMinimumPostViewTime(bootParamsData.minimumPostViewTime),
      );
    }
    yield put(homeWidgetActions.fetchHomePageWidget());

    /* below code should be non blocking */

    yield fork(function* () {
      const isSignupSessionVideo =
        yield AsyncStorage.getItem("signupSessionVideo");
      if (isSignupSessionVideo) {
        yield AsyncStorage.setItem("startSession", "true");
      }

      const value = yield AsyncStorage.getItem("cmNumberViewCount");
      const NUM_TIMES_WA_NUMBER_PROMPT_VISIBLE = yield select(
        (state) => state?.configReducer?.NUM_TIMES_WA_NUMBER_PROMPT_VISIBLE,
      );
      if (!value) {
        yield AsyncStorage.setItem("cmNumberViewCount", "1");
      } else if (parseInt(value, 10) < NUM_TIMES_WA_NUMBER_PROMPT_VISIBLE) {
        yield AsyncStorage.setItem(
          "cmNumberViewCount",
          `${parseInt(value, 10) + 1}`,
        );
      } else {
        yield AsyncStorage.setItem("cmNumberViewCount", "999");
      }
    });
    yield put(authActions.restoreToken({ token }));
    yield put(authActions.setFetchMeCompleted());
    yield fork(setAppConfigs); // call after token is saved in the redux
    yield fork(notificationPermission);
    yield call(trackVisibleApps);
  } catch (e) {
    Sentry.captureException(e);
    console.log("sync failed", e);
    yield put(actions.failureSyncUser(e));
  }
}

export default function* root() {
  yield all([
    takeLatest(INVOKE_APP_SETUP, invokeAppSetup),
    takeLatest(GET_APP_SETTINGS, getAppSettingsSaga),
    takeLatest(GET_APP_BOOT_PARAMS, getAppBootParamsSaga),
    takeLatest(SYNC_USER_ACTIONS.FETCH_SYNC_USER, fetchSyncUser),
    takeLatest(SET_LANGUAGE, fetchSyncUser),
    takeLatest(CLEAR_APP_SETUP, clearAppSetup),
    takeLatest(SET_APPLINK, setAppLink),
  ]);
}
